import { UserRole } from '@lmc/ts-common'
import { PermissionId } from '../../../constants/permissionIds'
import type { ToggleConfig } from './types'
import { CapabilityKey } from './types'

// labelKey / copyKey strings are the UPPER_SNAKE_CASE keys from teamConstants
// (added in Task 3 of the plan). `getKeys(teamTranslations)` returns
// `{ KEY: 'KEY' }`, so the constant value equals its key — passing the string
// straight into t() resolves the registered copy.
//
// `permissionId` is the backend ObjectId (from `constants/permissionIds.ts`).
// The wizard writes these directly into `user.permissions` on POST, so they
// must be the canonical ObjectIds — sending the human-readable code (e.g.
// "MANAGE_SAFEGUARDING") trips Mongoose's CastError when it tries to cast to
// [ObjectId].
const TOGGLE_DEFS: Record<CapabilityKey, Omit<ToggleConfig, 'defaultValue'>> = {
  [CapabilityKey.SafeguardingOversight]: {
    key: CapabilityKey.SafeguardingOversight,
    labelKey: 'CAPABILITY_SAFEGUARDING_OVERSIGHT_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_SAFEGUARDING_OVERSIGHT_BULLET1' }
    ],
    permissionId: PermissionId.MANAGE_SAFEGUARDING
  },
  [CapabilityKey.InsightsHubAccess]: {
    key: CapabilityKey.InsightsHubAccess,
    labelKey: 'CAPABILITY_INSIGHTS_HUB_LABEL',
    bullets: [{ variant: 'tick', copyKey: 'CAPABILITY_INSIGHTS_HUB_BULLET1' }],
    permissionId: PermissionId.VIEW_INSIGHTS_HUB
  },
  [CapabilityKey.TeamManagement]: {
    key: CapabilityKey.TeamManagement,
    labelKey: 'CAPABILITY_TEAM_MANAGEMENT_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_TEAM_MANAGEMENT_BULLET1' },
      { variant: 'tick', copyKey: 'CAPABILITY_TEAM_MANAGEMENT_BULLET2' },
      { variant: 'cross', copyKey: 'CAPABILITY_TEAM_MANAGEMENT_BULLET3' }
    ],
    permissionId: PermissionId.VIEW_AND_RESEND_TEAM_MEMBERS
  },
  [CapabilityKey.ServiceUserManagement]: {
    key: CapabilityKey.ServiceUserManagement,
    labelKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_LABEL',
    bullets: [
      {
        variant: 'tick',
        copyKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_BULLET1'
      },
      {
        variant: 'tick',
        copyKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_BULLET_DOCUMENTATION'
      },
      {
        variant: 'tick',
        copyKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_BULLET_RISK'
      },
      {
        variant: 'cross',
        copyKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_BULLET2'
      }
    ],
    // The two extra bullets (edit Documentation records, archive risk
    // assessments) are granted by this single permission once CM-3517 folds them
    // into CREATE_AND_EDIT_SERVICE_USER_DATA on the backend. This frontend (copy +
    // the bundle convert-on-edit in buildUpdatePayload) depends on CM-3517 being
    // deployed; the feature is flag-gated, so sequence the backend first.
    permissionId: PermissionId.CREATE_AND_EDIT_SERVICE_USER_DATA
  },
  [CapabilityKey.IncidentManagement]: {
    key: CapabilityKey.IncidentManagement,
    labelKey: 'CAPABILITY_INCIDENT_MANAGEMENT_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_INCIDENT_MANAGEMENT_BULLET1' }
    ],
    permissionId: PermissionId.CREATE_AND_MANAGE_INCIDENTS
  },
  [CapabilityKey.DownloadCentreAccess]: {
    key: CapabilityKey.DownloadCentreAccess,
    labelKey: 'CAPABILITY_DOWNLOAD_CENTRE_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_DOWNLOAD_CENTRE_BULLET1' }
    ],
    permissionId: PermissionId.VIEW_REPORTS_AND_EXPORT_HOME_DATA
  },
  [CapabilityKey.MedicationManagement]: {
    key: CapabilityKey.MedicationManagement,
    labelKey: 'CAPABILITY_MEDICATION_MANAGEMENT_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_MEDICATION_MANAGEMENT_BULLET1' }
    ]
    // no permissionId — writes to user.medicationPermission boolean instead
  }
}

const ROLE_TOGGLES: Partial<
  Record<UserRole, Array<{ key: CapabilityKey; defaultValue: boolean }>>
> = {
  [UserRole.careHomeAdmin]: [
    { key: CapabilityKey.SafeguardingOversight, defaultValue: false },
    { key: CapabilityKey.MedicationManagement, defaultValue: true }
  ],
  [UserRole.management]: [
    { key: CapabilityKey.SafeguardingOversight, defaultValue: false },
    { key: CapabilityKey.InsightsHubAccess, defaultValue: false },
    { key: CapabilityKey.MedicationManagement, defaultValue: true }
  ],
  // Group Admin / Group Manager have no add-on capabilities (CM-3868): they
  // operate at group level and shouldn't be granted site-level add-ons here, so
  // the "Add-on capabilities" section is hidden for them (roleHasStep3 → false),
  // matching IT & Audit View. This reverses CM-3775, which had briefly offered
  // Safeguarding Oversight to these roles.
  [UserRole.seniorCarer]: [
    { key: CapabilityKey.TeamManagement, defaultValue: false },
    { key: CapabilityKey.ServiceUserManagement, defaultValue: false },
    { key: CapabilityKey.IncidentManagement, defaultValue: false },
    { key: CapabilityKey.DownloadCentreAccess, defaultValue: false },
    { key: CapabilityKey.MedicationManagement, defaultValue: true }
  ],
  [UserRole.carer]: [
    { key: CapabilityKey.MedicationManagement, defaultValue: false } // Figma AC node 7965:45675
  ]
}

// Accept `string` (not just `UserRole`) to handle all role values cleanly.
export const roleHasStep3 = (role: string): boolean =>
  (ROLE_TOGGLES[role as UserRole]?.length ?? 0) > 0

// CM-3866: authoritative check for whether a role is eligible to hold the
// Safeguarding Oversight capability. Derived from the same per-role toggle map
// the wizard/edit panel use (Admin, Management and the two group roles are
// eligible; Senior Carer, Carer, IT and Audit View are not). The backend leaves
// stale MANAGE_SAFEGUARDING on `permissions` after a downgrade, so consumers use
// this to suppress the safeguarding chip for roles that can no longer hold it.
export const roleCanHoldSafeguarding = (role?: string): boolean =>
  (ROLE_TOGGLES[role as UserRole] ?? []).some(
    ({ key }) => key === CapabilityKey.SafeguardingOversight
  )

export const roleCanHoldInsightsHub = (role?: string): boolean =>
  role === UserRole.careHomeAdmin ||
  (ROLE_TOGGLES[role as UserRole] ?? []).some(
    ({ key }) => key === CapabilityKey.InsightsHubAccess
  )

export const getCapabilityTogglesForRole = (role: string): ToggleConfig[] =>
  (ROLE_TOGGLES[role as UserRole] ?? []).map(({ key, defaultValue }) => ({
    ...TOGGLE_DEFS[key],
    defaultValue
  }))

export type CapabilityVisibility = { isEmar: boolean }

// CM-4109: Medication Management is meaningless to a service without eMAR. The
// legacy edit form only ever showed it when `features.eMAR` was on; the new
// roles & permissions screens surfaced it to everyone, which confuses non-eMAR
// services. Filter it out of the *displayed* toggle list.
//
// DISPLAY ONLY: buildCreatePayload / buildUpdatePayload keep reading the
// unfiltered list and `data.capabilities` (seeded from getDefaultCapabilities /
// buildInitialData), so the medicationPermission we send is exactly what we
// sent before — a hidden toggle still submits its default / the carer's
// stored value.
export const getVisibleCapabilityTogglesForRole = (
  role: string,
  { isEmar }: CapabilityVisibility
): ToggleConfig[] =>
  getCapabilityTogglesForRole(role).filter(
    ({ key }) => isEmar || key !== CapabilityKey.MedicationManagement
  )

// Display counterpart of roleHasStep3: whether the role has any capability this
// service can actually see. Carer's only capability is Medication Management, so
// a non-eMAR service gets no add-on step (wizard) / section (edit panel) at all.
export const roleHasVisibleCapabilities = (
  role: string,
  visibility: CapabilityVisibility
): boolean => getVisibleCapabilityTogglesForRole(role, visibility).length > 0

export const getDefaultCapabilities = (
  role: string
): Partial<Record<CapabilityKey, boolean>> =>
  Object.fromEntries(
    getCapabilityTogglesForRole(role).map((t) => [t.key, t.defaultValue])
  )

// Reverse lookup the Edit-permissions side panel needs: given a backend
// permission ObjectId, find the CapabilityKey that carries it. Only the
// TOGGLE_DEFS entries with a `permissionId` participate (medicationManagement
// is a boolean field, not a permission, so it never matches).
const PERMISSION_ID_TO_CAPABILITY_KEY: Partial<Record<string, CapabilityKey>> =
  Object.fromEntries(
    (Object.values(TOGGLE_DEFS) as Omit<ToggleConfig, 'defaultValue'>[])
      .filter((def) => Boolean(def.permissionId))
      .map((def) => [def.permissionId as string, def.key])
  )

export const getCapabilityKeyForPermissionId = (
  permissionId: string
): CapabilityKey | undefined => PERMISSION_ID_TO_CAPABILITY_KEY[permissionId]
